import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import { format } from 'date-fns'
import {
  formatCurrency,
  formatPayoutWithDecimals,
  getMaxDecimalPlaces,
} from '../../shared/formats'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { PaymentRow, PaymentRowType, Scenario } from '../../shared/types'
import { FeatureFlagName } from '../../../api/feature-flags-api.ts'

interface Props {
  scenario: Scenario
  maxRows: number
}

export default function PayoutScheduleTable({ scenario, maxRows }: Props) {
  const theme = useTheme()

  const { flags } = useFeatureFlags()
  const useDollarAmounts =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT] ?? false

  const shouldUseDollarAmount = (row: PaymentRow) =>
    useDollarAmounts && row.inputType === PaymentRowType.DOLLAR

  const getPayoutValue = (row: PaymentRow): number =>
    useDollarAmounts ? (row.payoutAmount ?? 0) : (row.payoutPercentage ?? 0)

  return (
    <Table
      aria-label={`scenario-${scenario.id}-payment-schedule`}
      size="small"
      sx={{
        mb: 1,
        '& th': {
          backgroundColor: theme.palette.grey[50],
          fontWeight: 'bold',
          color: theme.palette.primary.light,
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '50%' }}>Payout</TableCell>
          <TableCell sx={{ width: '50%' }}>Payment Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(() => {
          const sortedSchedule = scenario.paymentSchedule
            .slice()
            .sort((a, b) => {
              if (a.paymentDate == null || b.paymentDate == null) return 0
              const dateA = new Date(a.paymentDate)
              const dateB = new Date(b.paymentDate)
              return dateA.getTime() - dateB.getTime()
            })

          // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
          const payoutDecimals = getMaxDecimalPlaces(
            sortedSchedule.map((row) => getPayoutValue(row))
          )

          return Array.from({ length: maxRows }).map((_, i) => {
            const row = sortedSchedule[i]
            const paymentDate =
              row?.paymentDate != null ? new Date(row.paymentDate) : null
            return (
              <TableRow
                key={`${scenario.id}-${i}`}
                sx={{
                  height: 32,
                  backgroundColor:
                    row != null && i % 2 === 1
                      ? theme.palette.grey[50]
                      : 'transparent',
                }}
              >
                {row != null ? (
                  <>
                    <TableCell
                      sx={{
                        color: 'black',
                        width: '50%',
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: theme.palette.grey[200],
                      }}
                    >
                      {shouldUseDollarAmount(row)
                        ? formatCurrency(row.payoutAmount)
                        : formatPayoutWithDecimals(
                            getPayoutValue(row),
                            payoutDecimals
                          )}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '50%',
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: theme.palette.grey[200],
                      }}
                    >
                      {paymentDate != null
                        ? format(
                            new Date(
                              paymentDate.valueOf() +
                                paymentDate.getTimezoneOffset() * 60 * 1000
                            ),
                            'MM/dd/yyyy'
                          )
                        : ''}
                    </TableCell>
                  </>
                ) : (
                  <TableCell
                    colSpan={2}
                    sx={{ borderBottom: 'none', p: 0, height: 32 }}
                  />
                )}
              </TableRow>
            )
          })
        })()}
      </TableBody>
    </Table>
  )
}
